import { hashWords } from "daedalus-core";
import { encodeUtf8 } from "../protocol/utf8.ts";

/**
 * The rules version (ADR-069 decision 7): `r-` and 8 lower-case hex digits naming the tables' content, the same
 * on the map server and the page, which is what `welcome` carries and the page compares with its own.
 */

const RULES_SEED = 0;
const BYTES_PER_WORD = 4;
const BITS_PER_BYTE = 8;
const HEX_DIGITS = 8;

/**
 * Writes JSON data in its canonical form: every record's keys in sorted order, every list in its order, and every
 * string, number, boolean and null as `JSON.stringify` writes it. Two values holding the same data give one text
 * whatever order their keys were written in.
 *
 * @param value JSON data: null, a boolean, a finite number, a string, or lists and plain records of them.
 * @returns The canonical text.
 * @throws TypeError when the value holds something `JSON.stringify` cannot write, such as a function.
 * @example
 * canonicalJson({ b: 1, a: [true, null] });
 * // '{"a":[true,null],"b":1}'
 */
export function canonicalJson(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map((item: unknown) => canonicalJson(item)).join(",")}]`;
  }
  if (typeof value === "object" && value !== null) {
    const record = value as Readonly<Record<string, unknown>>;
    return `{${Object.keys(record)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${canonicalJson(record[key])}`)
      .join(",")}}`;
  }
  const text: unknown = JSON.stringify(value);
  if (typeof text !== "string") {
    throw new TypeError(`canonicalJson takes JSON data only, and was handed a ${typeof value}`);
  }
  return text;
}

/**
 * The shared seeded hash (`hashWords`, xxHash32) of a text: its UTF-8 bytes packed four to a little-endian word after a
 * word holding the byte count, so two texts that share a prefix of whole words still differ.
 *
 * @param text The text.
 * @param seed The hash's seed, an unsigned 32-bit integer; 0 unless given.
 * @returns The unsigned 32-bit digest, the same on every engine.
 * @example
 * textDigest("prop-179");
 * // one unsigned 32-bit integer, the same on the page and the map server
 */
export function textDigest(text: string, seed = RULES_SEED): number {
  const bytes = encodeUtf8(text);
  const words = new Uint32Array(1 + Math.ceil(bytes.length / BYTES_PER_WORD));
  words[0] = bytes.length;
  for (let index = 0; index < bytes.length; index += 1) {
    const at = 1 + Math.floor(index / BYTES_PER_WORD);
    words[at] = ((words[at] as number) | ((bytes[index] as number) << ((index % BYTES_PER_WORD) * BITS_PER_BYTE))) >>> 0;
  }
  return hashWords(seed, words, words.length);
}

/**
 * Names a set of tables by their content: `textDigest` at seed 0 over the canonical form.
 *
 * @param tables The tables, as JSON data; the loader hands it the tables the validator built.
 * @returns `r-` and the digest as 8 lower-case hex digits.
 * @throws TypeError when the tables hold something that is not JSON data.
 * @example
 * rulesVersion({});
 * // "r-" and 8 hex digits, the same on every engine
 */
export function rulesVersion(tables: unknown): string {
  return `r-${textDigest(canonicalJson(tables)).toString(16).padStart(HEX_DIGITS, "0")}`;
}
